/**
 * Web Push on this device. The choice is kept per person in localStorage, so
 * a shared phone does not hand one person's notifications to the next: when
 * someone who has not opted in opens Hearth, a subscription left by someone
 * else is dropped. Delivery is services/push.ts on the API.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post } from './api.ts';
import { isIos, isStandalone } from './pwa.ts';

/** `install-first`: iPhone/iPad in a browser tab, where Web Push only works after Add to Home Screen. */
export type PushState = 'unsupported' | 'install-first' | 'blocked' | 'off' | 'on';
type Choice = 'on' | 'off' | { later: number };

const ASK_AGAIN_MS = 14 * 86_400_000;
const choiceKey = (userId: string) => `hearth.push.${userId}`;

function choice(userId: string): Choice | null {
  try {
    const v = localStorage.getItem(choiceKey(userId));
    if (v === 'on' || v === 'off') return v;
    if (v?.startsWith('later:')) return { later: Number(v.slice(6)) };
  } catch {
    /* private mode */
  }
  return null;
}

function remember(userId: string, c: Choice) {
  try {
    localStorage.setItem(choiceKey(userId), typeof c === 'string' ? c : `later:${c.later}`);
  } catch {
    /* private mode */
  }
}

let config: Promise<{ enabled: boolean; publicKey: string | null }> | null = null;
const pushConfig = () =>
  (config ??= get<{ enabled: boolean; publicKey: string | null }>('/api/push/config').catch(() => {
    config = null;
    return { enabled: false, publicKey: null };
  }));

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

function fromBase64Url(s: string) {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
}

const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((x, i) => x === b[i]);

/** This browser's subscription for Hearth's current key pair (an old pair's is replaced). */
async function subscribe(): Promise<PushSubscription> {
  const ready = Promise.race([navigator.serviceWorker.ready, new Promise<never>((_, no) => setTimeout(() => no(new Error('The app is still starting. Try again in a moment.')), 10_000))]);
  const [{ publicKey }, reg] = await Promise.all([pushConfig(), ready]);
  if (!publicKey) throw new Error('Notifications on devices are switched off for Hearth.');
  const key = fromBase64Url(publicKey);
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    const current = existing.options.applicationServerKey;
    if (current && sameBytes(new Uint8Array(current), key)) return existing;
    await existing.unsubscribe();
  }
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

const register = (sub: PushSubscription) => post('/api/push/subscriptions', sub.toJSON() as Record<string, unknown>);
const unregister = (endpoint: string) => api('/api/push/subscriptions', { method: 'DELETE', body: { endpoint } });

export async function pushState(userId: string): Promise<PushState> {
  if (isIos() && !isStandalone()) return 'install-first';
  if (!supported() || !(await pushConfig()).enabled) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  return Notification.permission === 'granted' && choice(userId) === 'on' && (await currentSubscription()) ? 'on' : 'off';
}

/** Call from a tap: Safari only asks for permission in response to one. */
export async function enablePush(userId: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off';
  await register(await subscribe());
  remember(userId, 'on');
  return 'on';
}

export async function disablePush(userId: string): Promise<PushState> {
  remember(userId, 'off');
  const sub = await currentSubscription();
  if (sub) {
    // Unsubscribing in the browser alone is enough: the push service then answers 410 and the API drops the row.
    await unregister(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  }
  return 'off';
}

/**
 * On each signed-in start: re-register this device (it refreshes the session
 * it belongs to, and recovers from a dropped row), or drop a subscription
 * this person never made.
 */
export async function syncPush(userId: string): Promise<void> {
  if (!supported() || (isIos() && !isStandalone())) return;
  const sub = await currentSubscription();
  if (choice(userId) !== 'on') {
    if (sub) await sub.unsubscribe().catch(() => false);
    return;
  }
  if (Notification.permission !== 'granted' || !(await pushConfig()).enabled) return;
  await register(await subscribe()).catch(() => undefined);
}

/** Before signing out: stop this person's notifications here. Their choice stays, so signing in again resumes them. */
export async function forgetPushDevice(): Promise<void> {
  const sub = await currentSubscription().catch(() => null);
  if (sub) await unregister(sub.endpoint).catch(() => undefined);
}

export async function sendTestPush(): Promise<void> {
  const sub = await currentSubscription();
  await post('/api/push/test', sub ? { endpoint: sub.endpoint } : {});
}

// ---------------------------------------------------------------------------
// The opt-in prompt: after a sign-in, never on a plain page load
// ---------------------------------------------------------------------------

const SIGNED_IN = 'hearth.signedIn';

/** Called by the sign-in form. */
export function noteSignIn() {
  try {
    sessionStorage.setItem(SIGNED_IN, '1');
  } catch {
    /* private mode */
  }
}

function signedInThisVisit() {
  try {
    return sessionStorage.getItem(SIGNED_IN) === '1';
  } catch {
    return false;
  }
}

function settlePrompt() {
  try {
    sessionStorage.removeItem(SIGNED_IN);
  } catch {
    /* private mode */
  }
}

export function shouldAsk(userId: string): boolean {
  if (!signedInThisVisit()) return false;
  const c = choice(userId);
  return c === null || (typeof c === 'object' && Date.now() - c.later > ASK_AGAIN_MS);
}

export function askLater(userId: string) {
  remember(userId, { later: Date.now() });
  settlePrompt();
}

/** Shared state for the prompt and the switch in the user menu. */
export function usePush(userId: string) {
  const qc = useQueryClient();
  const key = ['push', userId];
  const q = useQuery({ queryKey: key, queryFn: () => pushState(userId), staleTime: Infinity, retry: false });
  const set = (s: PushState) => qc.setQueryData(key, s);
  return {
    state: q.data,
    enable: async () => {
      settlePrompt();
      const s = await enablePush(userId);
      set(s);
      return s;
    },
    disable: async () => {
      settlePrompt();
      const s = await disablePush(userId);
      set(s);
      return s;
    },
  };
}
